// =====================================================
// Types pour le système de permissions
// =====================================================

export type RoleName = 'vendeur' | 'admin' | 'gerant' | 'magasin';

export type MenuCategory = 'navbar' | 'sidebar' | 'homepage';

export type MenuKey =
  // Navbar
  | 'menu_principal'
  | 'ventes'
  | 'listes'
  | 'livraisons'
  | 'statistiques'
  | 'comptabilite'
  | 'ca_du_jour'
  | 'marketing_studio'
  | 'contact_messages'
  | 'sav_requests'
  | 'gestion'
  | 'payment_audit'
  | 'orders2'
  // Navbar — sous-items dropdown, clés dédiées (chaque ligne de la matrice de
  // permissions doit être contrôlable indépendamment, sans clé partagée)
  | 'ventes_devis'
  | 'ventes_factures'
  | 'ventes_credit_notes'
  | 'ventes_orders'
  | 'ventes_orders2'
  | 'listes_clients'
  | 'listes_fournisseurs'
  | 'listes_articles'
  | 'statistiques_par_periode'
  | 'statistiques_par_vendeur'
  | 'gestion_achats'
  | 'gestion_commander_marchandises'
  | 'gestion_commandes_attente'
  | 'gestion_paiements_fournisseurs'
  | 'gestion_inventaire'
  | 'gestion_inventaire_2023'
  | 'comptabilite_orders2'
  | 'comptabilite_acomptes'
  | 'comptabilite_credit_notes'
  | 'comptabilite_factures_a_payer'
  | 'comptabilite_generale'
  // Sidebar
  | 'home'
  | 'orders'
  | 'products'
  | 'clients'
  | 'deliveries'
  | 'statistics'
  | 'accounting'
  | 'payments'
  // Homepage
  | 'new_order'
  | 'new_order2'
  | 'factures'
  | 'accompte_list'
  | 'credit_notes'
  | 'client_management'
  | 'articles'
  | 'search'
  | 'after_sales'
  | 'deliveries_services'
  // IA
  | 'ai_chat';

export interface MenuPermission {
  id: string;
  role_name: RoleName;
  menu_key: MenuKey;
  menu_category: MenuCategory;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserPermissions {
  menu_key: MenuKey;
  menu_category: MenuCategory;
  is_enabled: boolean;
}

export interface MenuDefinition {
  key: MenuKey;
  label: string;
  category: MenuCategory;
  description?: string;
}

// Définition de tous les menus disponibles
export const MENU_DEFINITIONS: MenuDefinition[] = [
  // Navbar
  { key: 'menu_principal', label: 'Menu Principal', category: 'navbar' },
  { key: 'ventes', label: 'Ventes', category: 'navbar' },
  { key: 'listes', label: 'Listes', category: 'navbar' },
  { key: 'livraisons', label: 'Livraisons', category: 'navbar' },
  { key: 'statistiques', label: 'Statistiques', category: 'navbar' },
  { key: 'comptabilite', label: 'Comptabilité', category: 'navbar' },
  { key: 'ca_du_jour', label: 'CA du jour', category: 'navbar' },
  { key: 'marketing_studio', label: 'Marketing Studio', category: 'navbar' },
  {
    key: 'contact_messages',
    label: 'Messages du site',
    category: 'navbar',
    description:
      "Boîte de réception du formulaire Contact eurodecor-meubles.be (icône de l'en-tête)",
  },
  {
    key: 'sav_requests',
    label: 'Demandes SAV du site',
    category: 'navbar',
    description: "Demandes SAV déposées sur eurodecor-meubles.be, avec photos (icône de l'en-tête)",
  },
  {
    key: 'gestion',
    label: 'Gestion',
    category: 'navbar',
    description: 'Réservé aux gérants et administrateurs',
  },
  {
    key: 'payment_audit',
    label: 'Surveillance des acomptes',
    category: 'navbar',
    description: 'Audit des suppressions de paiements',
  },
  {
    key: 'orders2',
    label: 'Commandes V2',
    category: 'navbar',
    description:
      'Bons de commande client — version 2 (dropdown Comptabilité) — legacy, remplacée par ventes_orders2 / comptabilite_orders2',
  },

  // Navbar — sous-items dropdown Ventes
  {
    key: 'ventes_devis',
    label: 'Devis',
    category: 'navbar',
    description: 'Sous-item dropdown Ventes',
  },
  {
    key: 'ventes_factures',
    label: 'Factures',
    category: 'navbar',
    description: 'Sous-item dropdown Ventes',
  },
  {
    key: 'ventes_credit_notes',
    label: 'Notes de crédit',
    category: 'navbar',
    description: 'Sous-item dropdown Ventes',
  },
  {
    key: 'ventes_orders',
    label: 'Bons de commande 1',
    category: 'navbar',
    description: 'Sous-item dropdown Ventes',
  },
  {
    key: 'ventes_orders2',
    label: 'Bons de commande 2',
    category: 'navbar',
    description: 'Sous-item dropdown Ventes',
  },

  // Navbar — sous-items dropdown Listes
  {
    key: 'listes_clients',
    label: 'Clients',
    category: 'navbar',
    description: 'Sous-item dropdown Listes',
  },
  {
    key: 'listes_fournisseurs',
    label: 'Fournisseurs',
    category: 'navbar',
    description: 'Sous-item dropdown Listes',
  },
  {
    key: 'listes_articles',
    label: 'Articles',
    category: 'navbar',
    description: 'Sous-item dropdown Listes',
  },

  // Navbar — sous-items dropdown Statistiques
  {
    key: 'statistiques_par_periode',
    label: 'Statistiques par période',
    category: 'navbar',
    description: 'Sous-item dropdown Statistiques',
  },
  {
    key: 'statistiques_par_vendeur',
    label: 'Statistiques par vendeur',
    category: 'navbar',
    description: 'Sous-item dropdown Statistiques',
  },

  // Navbar — sous-items dropdown Gestion
  {
    key: 'gestion_achats',
    label: "Toutes les factures d'achat",
    category: 'navbar',
    description: 'Sous-item dropdown Gestion',
  },
  {
    key: 'gestion_commander_marchandises',
    label: 'Commander des marchandises',
    category: 'navbar',
    description: 'Sous-item dropdown Gestion',
  },
  {
    key: 'gestion_commandes_attente',
    label: 'Commandes en attente',
    category: 'navbar',
    description: 'Sous-item dropdown Gestion',
  },
  {
    key: 'gestion_paiements_fournisseurs',
    label: 'Paiements fournisseurs',
    category: 'navbar',
    description: 'Sous-item dropdown Gestion',
  },
  {
    key: 'gestion_inventaire',
    label: 'Inventaire des articles',
    category: 'navbar',
    description: 'Sous-item dropdown Gestion',
  },
  {
    key: 'gestion_inventaire_2023',
    label: 'Inventaire 2023',
    category: 'navbar',
    description: 'Sous-item dropdown Gestion',
  },

  // Navbar — sous-items dropdown Comptabilité
  {
    key: 'comptabilite_orders2',
    label: 'Bons de commande 2',
    category: 'navbar',
    description: 'Sous-item dropdown Comptabilité',
  },
  {
    key: 'comptabilite_acomptes',
    label: 'Acomptes',
    category: 'navbar',
    description: 'Sous-item dropdown Comptabilité',
  },
  {
    key: 'comptabilite_credit_notes',
    label: 'Notes de crédit',
    category: 'navbar',
    description: 'Sous-item dropdown Comptabilité',
  },
  {
    key: 'comptabilite_factures_a_payer',
    label: 'Factures à payer',
    category: 'navbar',
    description: 'Sous-item dropdown Comptabilité',
  },
  {
    key: 'comptabilite_generale',
    label: 'Comptabilité générale',
    category: 'navbar',
    description: 'Sous-item dropdown Comptabilité (page non implémentée)',
  },

  // Sidebar
  { key: 'home', label: 'Accueil', category: 'sidebar' },
  { key: 'orders', label: 'Commandes', category: 'sidebar' },
  { key: 'products', label: 'Articles', category: 'sidebar' },
  { key: 'clients', label: 'Clients', category: 'sidebar' },
  { key: 'deliveries', label: 'Livraisons', category: 'sidebar' },
  { key: 'statistics', label: 'Statistiques', category: 'sidebar' },
  { key: 'accounting', label: 'Calculatrice', category: 'sidebar' },
  { key: 'payments', label: 'Acomptes', category: 'sidebar' },

  // Homepage
  { key: 'new_order', label: 'Nouvelle commande', category: 'homepage' },
  {
    key: 'new_order2',
    label: 'Bons de commande 2',
    category: 'homepage',
    description: 'Bouton dashboard vers les bons de commande V2 (orders2)',
  },
  { key: 'factures', label: 'Factures à établir', category: 'homepage' },
  { key: 'accompte_list', label: 'Liste des acomptes', category: 'homepage' },
  { key: 'credit_notes', label: 'Notes de crédit', category: 'homepage' },
  { key: 'client_management', label: 'Gestion clients', category: 'homepage' },
  { key: 'articles', label: 'Articles', category: 'homepage' },
  { key: 'search', label: 'Recherche', category: 'homepage' },
  { key: 'after_sales', label: 'Service après-vente', category: 'homepage' },
  { key: 'deliveries_services', label: 'Livraisons et services', category: 'homepage' },

  // IA
  {
    key: 'ai_chat',
    label: 'Assistant IA',
    category: 'sidebar',
    description: 'Accès au chat IA (rapport, requêtes, emails SAV)',
  },
];

// Super admin email
export const SUPER_ADMIN_EMAIL = 'daniel.gosset@hotmail.com';

// Rôles disponibles
export const ROLES: RoleName[] = ['vendeur', 'admin', 'gerant', 'magasin'];
